"use client";

import { useState, type ComponentType, type SVGProps } from "react";
import GetICMAppModal from "@/app/components/get-icm-app-modal";
import { DirectPaths } from "@/config/routes";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
import HandCoinsIcon from "@/app/assets/icons/who-is-icm-for/hand-coins";
import ChartNoAxesCombinedIcon from "@/app/assets/icons/who-is-icm-for/chart-no-axes-combined";
import ChartCandlestickIcon from "@/app/assets/icons/who-is-icm-for/chart-candlestick";
import ArrowDownToLineIcon from "@/app/assets/icons/who-is-icm-for/arrow-down-to-line";
import CoinsIcon from "@/app/assets/icons/who-is-icm-for/coins";
import ShieldCheckIcon from "@/app/assets/icons/who-is-icm-for/shield-check";

export type WhoIsICMForCard = {
    title: string;
    description: string;
    Icon: ComponentType<SVGProps<SVGSVGElement>>;
};

export type WhoIsICMForVariant = "home" | "broking";

export type WhoIsICMForProps = {
    /** `home` = default audience cards; `broking` = “Everything you need” */
    variant?: WhoIsICMForVariant;
    label?: string;
    heading?: string;
    cards?: readonly WhoIsICMForCard[];
    analyticsPath?: string;
    analyticsEvent?: string;
    headingId?: string;
    className?: string;
    showDownloadCta?: boolean;
};

/** Audience cards — Figma web 2181:7660 / mobile 2541:30624 */
const HOME_CARDS: readonly WhoIsICMForCard[] = [
    {
        title: "Savers",
        description:
            "Start investing with a small amount and grow through FDs, mutual funds, and digital gold.",
        Icon: HandCoinsIcon,
    },
    {
        title: "Investors",
        description:
            "Access many investment options including stocks, bonds, IPOs, and global stocks.",
        Icon: ChartNoAxesCombinedIcon,
    },
    {
        title: "Traders",
        description:
            "Leverage advanced trading tools, real-time insights, and features like F&O, MTF and Scalper.",
        Icon: ChartCandlestickIcon,
    },
];

const BROKING_CARDS: readonly WhoIsICMForCard[] = [
    {
        title: "Built for Active Traders & Scalpers",
        description:
            "Speed-first platform with fast order execution, scalper tools, and low-latency performance across NSE, BSE and MCX.",
        Icon: ChartNoAxesCombinedIcon,
    },
    {
        title: "Flat ₹9/Order or ₹999/Month",
        description:
            "India's most transparent pricing. No per-order charges on the Pro plan. No subscription fees on the Lite plan.",
        Icon: CoinsIcon,
    },
    {
        title: "SEBI Registered & Multi-Exchange",
        description:
            "Operated by InCred Money Broking Ltd (formerly known as South Asian Stocks Ltd.) SEBI Reg. INZ000164738. Member of NSE, BSE, MCX and NCDEX.",
        Icon: ShieldCheckIcon,
    },
];

const VARIANT_DEFAULTS = {
    home: {
        label: "WHO IS INCRED MONEY FOR?",
        heading: "Built for every kind of investor.",
        cards: HOME_CARDS,
        analyticsPath: DirectPaths.HOME,
        analyticsEvent: "Home_Explore_Broking_Fold2",
        headingId: "who-is-icm-for-heading",
    },
    broking: {
        label: "TRADE WITHOUT COMPROMISE",
        heading: "Everything you need.",
        cards: BROKING_CARDS,
        analyticsPath: DirectPaths.BROKING,
        analyticsEvent: "Broking_Everything_You_Need_Download",
        headingId: "broking-everything-you-need-heading",
    },
} as const;

/**
 * Who is ICM for — Figma desktop 2181:7653 / mobile 2541:30619
 * Reusable card-row section (120px padding, 48px header↔cards gap on desktop).
 *
 * Usage:
 *   <WhoIsICMFor />                    // home
 *   <WhoIsICMFor variant="broking" />  // broking page
 */
const WhoIsICMFor = ({
    variant = "home",
    label,
    heading,
    cards,
    analyticsPath,
    analyticsEvent,
    headingId,
    className = "",
    showDownloadCta = true,
}: WhoIsICMForProps) => {
    const defaults = VARIANT_DEFAULTS[variant];
    const resolvedLabel = label ?? defaults.label;
    const resolvedHeading = heading ?? defaults.heading;
    const resolvedCards = cards ?? defaults.cards;
    const resolvedAnalyticsPath = analyticsPath ?? defaults.analyticsPath;
    const resolvedAnalyticsEvent = analyticsEvent ?? defaults.analyticsEvent;
    const resolvedHeadingId = headingId ?? defaults.headingId;

    const [showAppModal, setShowAppModal] = useState(false);

    return (
        <section
            aria-labelledby={resolvedHeadingId}
            className={`w-full px-4 py-10 md:px-[120px] md:py-[120px] ${className}`.trim()}
        >
            <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-6 md:gap-12">
                <header className="flex w-full flex-col items-start gap-1 md:flex-row md:items-center md:justify-between md:gap-0">
                    <div className="flex w-full flex-col items-start gap-1 text-left md:max-w-[728px] md:gap-2">
                        <p className="w-full text-base font-semibold uppercase leading-[22px] tracking-[2px] text-brand-primary-500 md:text-xl md:leading-[26px] md:tracking-[1.6px]">
                            {resolvedLabel}
                        </p>
                        <h2
                            id={resolvedHeadingId}
                            className="w-full text-2xl font-bold leading-8 text-black md:max-w-[715px] md:text-5xl md:leading-none"
                        >
                            {resolvedHeading}
                        </h2>
                    </div>

                    {showDownloadCta ? (
                        <button
                            type="button"
                            onClick={() => {
                                void setAnalyticsEvent(resolvedAnalyticsPath, resolvedAnalyticsEvent);
                                setShowAppModal(true);
                            }}
                            className="hidden h-11 shrink-0 items-center justify-center gap-2 rounded-[var(--radius-m)] border border-cta-secondary-stroke bg-cta-secondary px-6 py-2.5 text-base font-medium leading-[22px] text-cta-secondary-text transition-colors hover:border-cta-secondary-stroke-hover hover:bg-cta-secondary-hover hover:text-cta-secondary-text-hover focus:border-cta-secondary-stroke-focus focus:bg-cta-secondary-focus focus:text-cta-secondary-text-focus md:inline-flex"
                        >
                            Download the app
                            <ArrowDownToLineIcon className="block size-full text-cta-secondary-icon" />
                        </button>
                    ) : null}
                </header>

                <ul className="flex w-full flex-col gap-5 md:flex-row md:items-stretch">
                    {resolvedCards.map(({ title, description, Icon }) => (
                        <li
                            key={title}
                            className="flex w-full flex-col gap-8 overflow-hidden rounded-2xl border border-[var(--colors-stroke-colors-card-stroke-color,#E6E6E6)] bg-white p-5 md:flex-1 md:gap-[31px] md:p-[19px]"
                        >
                            <Icon />
                            <div className="flex flex-col gap-3">
                                <h3 className="text-2xl font-bold leading-8 text-black md:text-[30px] md:leading-9">
                                    {title}
                                </h3>
                                <p className="text-base font-regular leading-[22px] text-[var(--colors-text-body-text-1,#767676)] md:text-xl md:leading-[26px]">
                                    {description}
                                </p>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>

            <GetICMAppModal open={showAppModal} onClose={() => setShowAppModal(false)} />
        </section>
    );
};

export default WhoIsICMFor;
